import { useTranslation } from 'react-i18next'

import ArrowLeftIcon from 'assets/icons/arrow-left.svg'
import ArrowRightIcon from 'assets/icons/arrow-right.svg'

import { Button } from 'components'

import styles from './PageControls.module.scss'
import PageNumber from './PageNumber'

export interface PageControlsProps {
  currentPage: number
  fullSize?: boolean
  onPageChange: (nextPage: number) => void
  totalPages: number
}

const getPagination = (currentPage: number, totalPages: number) => {
  const visiblePages = 3
  // Always show the first page
  const pages: (number | string)[] = [1]

  // Show ellipsis if the currentPage is far from the start
  if (currentPage > visiblePages) {
    pages.push('...')
  }

  // Determine the range of pages around the currentPage
  const startPage = Math.max(2, currentPage - 1)
  const endPage = Math.min(totalPages - 1, currentPage + 1)

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i)
  }

  // Show ellipsis if the currentPage is far from the end
  if (currentPage <= totalPages - visiblePages) {
    pages.push('...')
  }

  // Always show the last page
  if (totalPages > 1) {
    pages.push(totalPages)
  }

  return pages
}

const PageControls = ({
  currentPage,
  fullSize = true,
  onPageChange,
  totalPages,
}: PageControlsProps) => {
  const { t } = useTranslation('components')

  return (
    <div className={styles.pageControls} {...(fullSize ? { 'full-size': '' } : {})}>
      <Button
        disabled={currentPage === 1}
        leftIcon={<ArrowLeftIcon />}
        onClick={() => onPageChange(currentPage - 1)}
        small
        variant="tertiary"
      >
        {t('pagination.previous')}
      </Button>
      <div className={styles.pages}>
        {getPagination(currentPage, totalPages).map((page, index) => {
          const clickable = typeof page === 'number' && page !== currentPage
          return (
            <PageNumber
              active={page === currentPage}
              className={clickable ? styles.clickable : undefined}
              key={index}
              onClick={clickable ? () => onPageChange(page) : undefined}
            >
              {page}
            </PageNumber>
          )
        })}
      </div>
      <Button
        disabled={currentPage >= totalPages}
        rightIcon={<ArrowRightIcon />}
        onClick={() => onPageChange(currentPage + 1)}
        small
        variant="tertiary"
      >
        {t('pagination.next')}
      </Button>
    </div>
  )
}

export default PageControls
